import {
  Box,
  Button,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Typography,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { useState } from 'react';
import PageTitle from '../../components/page-title/PageTitle';
import { Container } from '../followup/FollowupList.styled';
import { AiFillCloseCircle } from 'react-icons/ai';
import toastr from 'toastr';
import { useEffect } from 'react';
import { useUserTypeDocumentsService } from '../../hooks/services/useUserTypeDocumentsService';
import { useTypeDocumentService } from '../../hooks/services/useTypeDocumentService';
import ITypeDocument from '../../interfaces/ITypeDocument';

interface IProps {
  closeDialog: () => void;
  isOpen: boolean;
  userId: string;
  userName: string;
}

const InternalUserTypeDocumentsAccess = (props: IProps) => {
  const [allDocumentTypes, setAllDocumentTypes] = useState<ITypeDocument[]>([]);
  const service = useUserTypeDocumentsService();
  const serviceTypeDocuments = useTypeDocumentService();
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUserTypeDocuments = async (userId: string) => {
    return await service
      .listByUserId(userId)
      .then(result => {
        return result; //
      })
      .catch(error => {
        toastr.error(error);
        return [];
      });
  };

  const loadData = async () => {
    if (!props.userId) return;

    const data = await fetchUserTypeDocuments(props.userId);

    if (data && data.length > 0) {
      setSelectedTypes(data.map(item => String(item.id)));
    } else {
      setSelectedTypes([]);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const fetchTypes = async () => {
      setLoading(true);

      const perPage = 100;
      const currentPage = 1;
      const orderField = 'description';
      const direction = 'asc';
      try {
        const types = await serviceTypeDocuments.list(perPage, currentPage, orderField, direction);
        setAllDocumentTypes(types.data);
      } catch (err) {
        toastr.error('Erro ao buscar tipos de documentos');
      } finally {
        setLoading(false);
      }
    };
    fetchTypes();
  }, []);

  const handleToggle = (id: string) => {
    setSelectedTypes(prev => (prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await service.update(props.userId, selectedTypes);
      toastr.success('Permissões atualizadas com sucesso!');
      props.closeDialog();
    } catch (err) {
      toastr.error('Erro ao atualizar permissões');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    props.closeDialog();
  };

  return (
    <Container>
      <PageTitle title="Permissões de Tipos de Documentos">
        <Tooltip title="Fechar" placement="left">
          <IconButton onClick={handleClose}>
            <AiFillCloseCircle />
          </IconButton>
        </Tooltip>
      </PageTitle>

      <Typography variant="inherit" sx={{ m: 2 }}>
        Selecione os tipos de documentos que o usuário {props.userName} pode acessar:
      </Typography>

      <List
        sx={{
          maxHeight: 350,
          overflowY: 'auto',
        }}
      >
        {allDocumentTypes.map(type => (
          <ListItem key={type.id} button onClick={() => handleToggle(String(type.id))}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={selectedTypes.includes(String(type.id))}
                tabIndex={-1}
                disableRipple
                inputProps={{ 'aria-labelledby': `checkbox-list-label-${type.id}` }}
              />
            </ListItemIcon>
            <ListItemText id={`checkbox-list-label-${type.id}`} primary={type.description} />
          </ListItem>
        ))}
      </List>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
        <Button onClick={handleClose} variant="contained" color="secondary" sx={{ marginRight: 2 }}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          Salvar
        </Button>
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default InternalUserTypeDocumentsAccess;
